import React, { useEffect, useState } from "react";
import { FolderPlus, Search, X } from "lucide-react";
import { offerApi } from "../../services/offerApi";

// Fenêtre « Nouveau chiffrage » / « Nouvelle proposition ». Un chiffrage se range dans une affaire ; un projet qui n'en
// a pas encore est proposé quand même et l'affaire est ouverte à la création (numéro suivant P-AAAA-N). Depuis la fiche
// projet (`project`), seules les affaires de ce projet sont montrées, avec « Nouvelle affaire » en plus.
const NewDocumentDialog = ({ kind, project = null, onClose, onCreated }) => {
  const proposal = kind === "proposal";
  const [query, setQuery] = useState("");
  const [affaires, setAffaires] = useState([]);
  const [bareProjects, setBareProjects] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [choice, setChoice] = useState(null); // { affaireId } ou { projectId, projectName }
  const [title, setTitle] = useState("");
  const [estimates, setEstimates] = useState([]);
  const [estimateId, setEstimateId] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const projectId = project?.id;

  useEffect(() => {
    const timer = setTimeout(() => {
      const affairesRequest = offerApi.affaires(projectId ? { project_id: projectId, limit: 30 } : { q: query, limit: 30 });
      const projectsRequest = projectId ? Promise.resolve({ projects: [] }) : offerApi.projectsWithoutAffaire({ q: query, limit: 10 });
      Promise.all([affairesRequest, projectsRequest])
        .then(([affaireData, projectData]) => {
          const found = affaireData.affaires || [];
          setAffaires(found);
          setBareProjects(projectData.projects || []);
          if (projectId && !loaded) {
            setChoice(found[0] ? { affaireId: found[0].id } : { projectId, projectName: project.name });
          }
          setLoaded(true);
        })
        .catch((err) => setError(err.message));
    }, projectId ? 0 : 250);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, projectId]);

  // Proposition : elle reprend les prix d'un chiffrage de la même affaire.
  const affaireId = choice?.affaireId;
  useEffect(() => {
    setEstimates([]);
    setEstimateId("");
    if (!proposal || !affaireId) return;
    offerApi.list({ affaire_id: affaireId, limit: 50 })
      .then((data) => {
        const items = data.items || [];
        setEstimates(items);
        setEstimateId(items[0] ? String(items[0].id) : "");
        if (items[0]) setTitle((current) => current || items[0].title);
      })
      .catch((err) => setError(err.message));
  }, [proposal, affaireId]);

  const submit = async (event) => {
    event.preventDefault();
    if (!choice || !title.trim()) return;
    setBusy(true);
    setError("");
    try {
      let targetId = choice.affaireId;
      if (!targetId) {
        const opened = await offerApi.openAffaire({ project_id: choice.projectId });
        targetId = opened.id;
        setChoice({ affaireId: targetId }); // en cas d'erreur juste après, on ne rouvre pas une deuxième affaire
      }
      if (proposal) {
        const created = await offerApi.createProposal({
          affaire_id: targetId, title: title.trim(), ...(estimateId ? { estimate_id: Number(estimateId) } : {}),
        });
        onCreated(`/tools/offres/propositions/${created.proposal.id}`);
      } else {
        const created = await offerApi.create({ affaire_id: targetId, title: title.trim() });
        onCreated(`/tools/offres/${created.estimate.id}`);
      }
    } catch (err) {
      setError(err.message);
      setBusy(false);
    }
  };

  const rowClass = (selected) => `flex w-full items-start justify-between gap-3 border-b border-slate-100 px-3 py-2 text-left text-sm last:border-0 ${
    selected ? "bg-blue-50" : "hover:bg-slate-50"}`;
  const nothing = loaded && affaires.length === 0 && bareProjects.length === 0 && !projectId;

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-slate-900/30 p-4">
      <form onSubmit={submit} className="w-full max-w-lg space-y-4 rounded-2xl bg-white p-6 shadow-xl">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-slate-950">{proposal ? "Nouvelle proposition" : "Nouveau chiffrage"}</h2>
            {project && <p className="text-sm text-slate-500">{project.name}</p>}
          </div>
          <button type="button" onClick={onClose} className="rounded-full p-1 text-slate-400 hover:bg-slate-100" aria-label="Fermer">
            <X className="h-5 w-5" />
          </button>
        </div>
        {!project && (
          <label className="block text-sm font-medium text-slate-700" htmlFor="offer-affaire-search">
            Affaire ou projet
            <div className="relative mt-1">
              <Search className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
              <input
                id="offer-affaire-search"
                autoFocus
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Numéro, projet ou client"
                className="w-full rounded-lg border border-slate-300 py-2 pl-9 pr-3 text-sm"
              />
            </div>
          </label>
        )}
        {project && <p className="text-sm font-medium text-slate-700">Affaire</p>}
        <div className="max-h-64 overflow-y-auto rounded-lg border border-slate-200">
          {nothing && <p className="p-3 text-sm text-slate-500">Aucune affaire ni aucun projet trouvé.</p>}
          {affaires.map((affaire) => (
            <button key={affaire.id} type="button" onClick={() => setChoice({ affaireId: affaire.id })}
              className={rowClass(choice?.affaireId === affaire.id)}>
              <span>
                <span className="font-medium text-slate-900">{affaire.number || `Affaire ${affaire.id}`}</span>
                <span className="block text-xs text-slate-500">
                  {[project ? affaire.status : affaire.project_name, project ? null : affaire.client_name].filter(Boolean).join(" · ")}
                </span>
              </span>
              {affaire.estimates_count > 0 && (
                <span className="shrink-0 text-xs text-slate-400">{affaire.estimates_count} chiffrage(s)</span>
              )}
            </button>
          ))}
          {project && loaded && (
            <button type="button" onClick={() => setChoice({ projectId: project.id, projectName: project.name })}
              className={rowClass(choice?.projectId === project.id)}>
              <span className="flex items-start gap-2">
                <FolderPlus className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
                <span>
                  <span className="font-medium text-slate-900">Nouvelle affaire</span>
                  <span className="block text-xs text-slate-500">Ouverte à la création, avec le numéro suivant</span>
                </span>
              </span>
            </button>
          )}
          {bareProjects.length > 0 && (
            <p className="border-b border-slate-100 bg-slate-50 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
              Projets sans affaire : elle sera ouverte à la création
            </p>
          )}
          {bareProjects.map((row) => (
            <button key={`p${row.project_id}`} type="button"
              onClick={() => setChoice({ projectId: row.project_id, projectName: row.project_name })}
              className={rowClass(choice?.projectId === row.project_id)}>
              <span className="flex items-start gap-2">
                <FolderPlus className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
                <span>
                  <span className="font-medium text-slate-900">{row.project_name}</span>
                  <span className="block text-xs text-slate-500">{row.client_name || "Sans client"}</span>
                </span>
              </span>
            </button>
          ))}
        </div>
        {proposal && affaireId && (
          <label className="block text-sm font-medium text-slate-700" htmlFor="offer-estimate">
            Chiffrage repris (prix et délais)
            <select id="offer-estimate" value={estimateId} onChange={(event) => setEstimateId(event.target.value)}
              className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm">
              {estimates.length === 0 && <option value="">Aucun chiffrage pour cette affaire</option>}
              {estimates.map((estimate) => <option key={estimate.id} value={estimate.id}>{estimate.title}</option>)}
            </select>
          </label>
        )}
        <label className="block text-sm font-medium text-slate-700" htmlFor="offer-title">
          Titre
          <input
            id="offer-title"
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            placeholder="Ex. POC tête jet d'encre à chariotage 3D"
            className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
          />
        </label>
        {error && <p className="text-sm text-red-600">{error}</p>}
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className="rounded-full px-4 py-2 text-sm text-slate-600 hover:bg-slate-100">
            Annuler
          </button>
          <button
            type="submit"
            disabled={!choice || !title.trim() || busy}
            className="rounded-full bg-slate-900 px-4 py-2 text-sm font-medium text-white disabled:opacity-40"
          >
            {proposal ? "Créer la proposition" : "Créer le chiffrage"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default NewDocumentDialog;
